'use client'
import {useState} from 'react'
import {useServerInsertedHTML} from 'next/navigation'
import {CacheProvider} from '@emotion/react'
import createCache from '@emotion/cache'
import {ThemeProvider} from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import theme from '../src/theme'

function EmotionCacheProvider({children}: {children: React.ReactNode}) {
  const [{cache, flush}] = useState(() => {
    const cache = createCache({key: 'mui-style'})
    cache.compat = true
    const prevInsert = cache.insert
    let inserted: string[] = []
    cache.insert = (...args: Parameters<typeof prevInsert>) => {
      const serialized = args[1]
      if (cache.inserted[serialized.name] === undefined) {
        inserted.push(serialized.name)
      }
      return prevInsert(...args)
    }
    const flush = () => {
      const prevInserted = inserted
      inserted = []
      return prevInserted
    }
    return {cache, flush}
  })

  useServerInsertedHTML(() => {
    const names = flush()
    if (names.length === 0) return null
    let styles = ''
    for (const name of names) {
      styles += cache.inserted[name]
    }
    return (
      <style
        key={cache.key}
        data-emotion={`${cache.key} ${names.join(' ')}`}
        dangerouslySetInnerHTML={{__html: styles}}
      />
    )
  })

  return <CacheProvider value={cache}>{children}</CacheProvider>
}

export default function ThemeRegistry({children}: {children: React.ReactNode}) {
  return (
    <EmotionCacheProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </EmotionCacheProvider>
  )
}
